import React, { useState, useEffect } from "react";
import { Row, Col, Card, Typography, Collapse, Spin, message, Button } from "antd";
import { Line, Bar } from "react-chartjs-2";
import { Chart as ChartJS, registerables } from "chart.js";
import axios from "axios";
import moment from "moment";
import { usePermissions } from "../App/PermissionContext";

import PageHeader from "../Layout/PageHeader";
import CodAnalysisFilters from "./CodAnalysisFilters";
import { PERMISSIONS } from "../../constants/roles";
import { DownloadOutlined } from "@ant-design/icons";

ChartJS.register(...registerables);

const endOfThisMonth = moment().endOf("month");
const threeMonthsAgo = moment().subtract(3, "months").startOf("month");

const CodAnalysis = () => {
  const [filters, setFilters] = useState({
    startDate: threeMonthsAgo,
    endDate: endOfThisMonth,
    codCurrencyCode: "RM",
  });

  const [monthlyStats, setMonthlyStats] = useState([]); // courier COD stats
  const [ndrStats, setNdrStats] = useState([]); // user & NDR stats
  const [stateStats, setStateStats] = useState([]); // user & NDR stats
  const [loading, setLoading] = useState(false);

  const { permissions } = usePermissions();

  useEffect(() => {
    const filterParams = {
      startDate: filters.startDate?.startOf("month")?.toISOString(),
      endDate: filters.endDate?.endOf("month")?.toISOString(),
      codCurrencyCode: filters.codCurrencyCode,
    };

    const fetchStats = async () => {
      setLoading(true);
      try {
        // COD stats
        const res1 = await axios.get(`/api/analysis/monthly-cod-order`, {
          params: { ...filterParams },
        });
        setMonthlyStats(res1.data?.data || []);

        // NDR stats
        const res2 = await axios.get(`/api/analysis/monthly-user-ndr`, {
          params: { ...filterParams },
        });
        setNdrStats(res2.data?.data || res2.data || []);

        // cod by state
        const res3 = await axios.get(`/api/analysis/monthly-cod-by-state`, {
          params: { ...filterParams },
        });
        const grouped = res3.data.data.reduce((acc, d) => {
          const year = d.year;
          const month = d.month.toString().padStart(2, "0");
          const yearMonth = `${year}-${month}`;
          const state = d.state;

          if (!acc[yearMonth]) acc[yearMonth] = {};
          if (!acc[yearMonth][state]) acc[yearMonth][state] = { delivered: 0, returned: 0 };
          acc[yearMonth][state].delivered += d.deliveredCodOrdersCount || 0;
          acc[yearMonth][state].returned += d.returnedCodOrdersCount || 0;

          return acc;
        }, {});
        setStateStats(grouped)

      } catch (error) {
        message.error(error.response?.data?.message || error.message, 0);
      }
      setLoading(false);
    };

    fetchStats();
  }, [filters]);

  const codLabels =
    monthlyStats[0]?.data?.map((stat) => `${stat.year}-${stat.month}`) || [];

  const orderCountDatasets =
    monthlyStats?.map((provider, i) => ({
      label: provider.serviceProvider,
      borderColor: `hsl(${i * 60}, 70%, 50%)`,
      backgroundColor: `hsl(${i * 60}, 70%, 50%)`,
      data: provider.data?.map((d) => d.orderCount) || [],
    })) || [];

  const revenueDatasets =
    monthlyStats?.map((provider, i) => ({
      label: provider.serviceProvider,
      borderColor: `hsl(${i * 60}, 60%, 45%)`,
      backgroundColor: `hsl(${i * 60}, 60%, 45%)`,
      data: provider.data?.map((d) => d.totalPrice) || [],
    })) || [];

  // ===== NDR (flat array) =====
  const ndrLabels = Array.isArray(ndrStats)
    ? ndrStats.map((stat) => `${stat.year}-${stat.month}`)
    : [];

  const userCountDataset = [
    {
      label: "Unique Users",
      type: "line",
      yAxisID: "B",
      borderColor: "hsl(10, 80%, 55%)",
      backgroundColor: "hsl(10, 80%, 55%)",
      tension: 0.3,
      fill: false,
      pointRadius: 5,
      pointHoverRadius: 7,
      borderWidth: 2.5,
      data: Array.isArray(ndrStats)
        ? ndrStats.map((d) => d.userCount || 0)
        : [],
      order: 10,
    },
    {
      label: "Total Orders",
      type: "bar",
      yAxisID: "A",
      backgroundColor: "hsla(200, 70%, 60%, 0.4)", // translucent blue
      borderColor: "hsl(200, 70%, 60%)",
      borderWidth: 1,
      data: Array.isArray(ndrStats)
        ? ndrStats.map((d) => d.ordersCount || 0)
        : [],
    },

  ];
  return (
    <React.Fragment>
      <PageHeader
        title="COD Analytics"
        childRoutes={[{ path: "/cod-analysis", breadcrumbName: "COD Analytics" }]}
      />

      <Collapse className="mb-4" style={{ marginBottom: "24px" }}>
        <Collapse.Panel
          header={`Filters (${filters.startDate.format(
            "MMM YYYY"
          )} - ${filters.endDate.format("MMM YYYY")}, ${filters.codCurrencyCode
            })`}
          key="filters"
        >
          <CodAnalysisFilters
            filters={filters}
            onFiltersChange={(filters) => setFilters(filters)}
          />
        </Collapse.Panel>
      </Collapse>


      <Card
        style={{ marginBottom: "24px" }}
        title={
          <Row justify="space-between" align="middle" className="items-center">
            <Typography.Title level={4} className="!mb-0">
              COD Orders by Couriers
            </Typography.Title>

            {permissions?.includes(PERMISSIONS.EXPORT_ORDER_LIST) && (
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={() => {
                  const params = new URLSearchParams({
                    startDate: filters.startDate,
                    endDate: filters.endDate,
                    codCurrencyCode: filters.codCurrencyCode
                  }).toString();

                  window.open(`/order/cod-order?${params}`, "_blank");
                }}
              >
                CSV
              </Button>
            )}
          </Row>
        }
        className="mb-6"
      >
        <Row gutter={16}>
          <Col md={12}>
            <Card size="small" title="Total Orders">
              {loading ? (
                <Spin />
              ) : (
                <Line
                  data={{ labels: codLabels, datasets: orderCountDatasets }}
                  options={{ interaction: { mode: "index" } }}
                />
              )}
            </Card>
          </Col>

          <Col md={12}>
            <Card size="small" title={`Total COD Amount (${filters.codCurrencyCode})`}>
              {loading ? (
                <Spin />
              ) : (
                <Line
                  data={{ labels: codLabels, datasets: revenueDatasets }}
                  options={{ interaction: { mode: "index" } }}
                />
              )}
            </Card>
          </Col>
        </Row>
      </Card>

      <Card
        style={{ marginBottom: "24px" }}
        title={
          <Row justify="space-between" align="middle" className="items-center">
            <Typography.Title level={4} className="!mb-0">
              User & Remitted NDR Analytics
            </Typography.Title>

            {permissions?.includes(PERMISSIONS.EXPORT_ORDER_LIST) && (
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={() => {
                  const params = new URLSearchParams({
                    startDate: filters.startDate,
                    endDate: filters.endDate,
                    sortBy: "remittedDate",
                    isNextDayRemittance: true,
                    isRemittance: true,
                    codCurrencyCode: filters.codCurrencyCode
                  }).toString();

                  window.open(`/order/cod-order?${params}`, "_blank");
                }}
              >
                CSV
              </Button>
            )}
          </Row>
        }
        className="mb-6"
      > <Row gutter={16}>
          <Col md={12}>
            <Card title="Total Orders  with Unique Users">
              {loading ? (
                <Spin />
              ) : (
                <Line
                  data={{ labels: ndrLabels, datasets: userCountDataset }}
                  options={{
                    responsive: true,
                    interaction: { mode: "index", intersect: false },
                    stacked: false,
                    plugins: {
                      legend: { position: "top" },
                      tooltip: {
                        mode: "index",
                        intersect: false,
                        callbacks: {
                          label: function (context) {
                            if (context.dataset.label.includes("Fee")) {
                              return `${context.dataset.label}: RM ${context.formattedValue}`;
                            }
                            return `${context.dataset.label}: ${context.formattedValue}`;
                          },
                        },
                      },
                      title: {
                        display: true,
                        text: "Monthly Summary: Total Orders  with Unique Users",
                      },
                    },
                    scales: {
                      A: {
                        type: "linear",
                        position: "left",
                        beginAtZero: true,
                        stacked: true,
                        title: { display: true, text: "Total Orders)" },
                      },
                      B: {
                        type: "linear",
                        position: "right",
                        beginAtZero: true,
                        grid: { drawOnChartArea: false },
                        title: { display: true, text: "Unique Users" },
                      },
                      x: {
                        title: { display: true, text: "Month (YYYY-MM)" },
                      },
                    },
                  }}
                />
              )}
            </Card>
          </Col>

          <Col md={12}>
            <Card title={`NDR Fee (${filters.codCurrencyCode}) with Unique Users`}>
              {loading ? (
                <Spin />
              ) : (
                <Line
                  data={{
                    labels: ndrLabels, datasets: [

                      {
                        label: "Total NDR Fee (RM)",
                        type: "bar",
                        yAxisID: "A",
                        backgroundColor: "hsla(150, 70%, 55%, 0.4)", // translucent green
                        borderColor: "hsl(150, 70%, 55%)",
                        borderWidth: 1,
                        data: Array.isArray(ndrStats)
                          ? ndrStats.map((d) => d.totalNdrFee || 0)
                          : [],
                      },
                      {
                        label: "Unique Users",
                        type: "line",
                        yAxisID: "B",
                        borderColor: "hsl(10, 80%, 55%)",
                        backgroundColor: "hsl(10, 80%, 55%)",
                        tension: 0.3,
                        fill: false,
                        pointRadius: 5,
                        pointHoverRadius: 7,
                        borderWidth: 2.5,
                        data: Array.isArray(ndrStats)
                          ? ndrStats.map((d) => d.userCount || 0)
                          : [],
                        order: 10,
                      },

                    ]
                  }}
                  options={{
                    responsive: true,
                    interaction: { mode: "index", intersect: false },
                    stacked: false,
                    plugins: {
                      legend: { position: "top" },
                      tooltip: {
                        mode: "index",
                        intersect: false,
                        callbacks: {
                          label: function (context) {
                            if (context.dataset.label.includes("Fee")) {
                              return `${context.dataset.label}: RM ${context.formattedValue}`;
                            }
                            return `${context.dataset.label}: ${context.formattedValue}`;
                          },
                        },
                      },
                      title: {
                        display: true,
                        text: `Monthly Summary: NDR Fee (${filters.codCurrencyCode}) with Unique Users`,
                      },
                    },
                    scales: {
                      A: {
                        type: "linear",
                        position: "left",
                        beginAtZero: true,
                        stacked: true,
                        title: { display: true, text: `NDR Fee (${filters.codCurrencyCode})` },
                      },
                      B: {
                        type: "linear",
                        position: "right",
                        beginAtZero: true,
                        grid: { drawOnChartArea: false },
                        title: { display: true, text: "Unique Users" },
                      },
                      x: {
                        title: { display: true, text: "Month (YYYY-MM)" },
                      },
                    },
                  }}
                />
              )}
            </Card>
          </Col>
        </Row></Card>

      <Card
        style={{ marginBottom: "24px" }}
        title={
          <Row justify="space-between" align="middle" className="items-center">
            <Typography.Title level={4} className="!mb-0">
              COD Orders by State
            </Typography.Title>
          </Row>
        }
        className="mb-6"
      > <Row gutter={16} style={{ marginBottom: "24px" }}>
          <Col md={24}>
            <Card title={
              <Row justify="space-between" align="middle" className="items-center">
                <Typography.Title level={4} className="!mb-0">
                  Delivered COD Orders by State
                </Typography.Title>

                {permissions?.includes(PERMISSIONS.EXPORT_ORDER_LIST) && (
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={() => {
                      const params = new URLSearchParams({
                        internalstatus: ["Delivered"],
                        startDate: filters.startDate,
                        endDate: filters.endDate,
                        codCurrencyCode: filters.codCurrencyCode
                      }).toString();

                      window.open(`/order/cod-order?${params}`, "_blank");
                    }}
                  >
                    CSV
                  </Button>
                )}
              </Row>
            }>
              {loading ? (
                <Spin />
              ) : (

                <Bar
                  data={{
                    labels: Object.keys(stateStats).sort(),
                    datasets: (() => {
                      const months = Object.keys(stateStats).sort();
                      const states = Array.from(
                        new Set(months.flatMap((m) => Object.keys(stateStats[m])))
                      );

                      return states.map((state, i) => ({
                        label: state,
                        data: months.map((m) => stateStats[m][state]?.delivered || 0),
                        borderColor: `hsl(${(i * 30) % 360}, 70%, 55%)`,
                        backgroundColor: `hsl(${(i * 30) % 360}, 70%, 55%)`,
                        fill: false,
                        tension: 0.3,
                        borderWidth: 2,
                        pointRadius: 3,
                      }));
                    })(),
                  }}
                  options={{
                    responsive: true,
                    interaction: { mode: "index", intersect: false },
                    plugins: {
                      legend: { position: "bottom" },
                      title: {
                        display: true,
                        text: "Delivered COD Orders by State (Monthly)",
                      },
                      tooltip: {
                        callbacks: {
                          label: (ctx) =>
                            `${ctx.dataset.label}: ${ctx.formattedValue}`,
                        },
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        title: { display: true, text: "Delivered Orders" },
                      },
                      x: {
                        title: { display: true, text: "Month (YYYY-MM)" },
                      },
                    },
                  }}
                />
              )}
            </Card>
          </Col>


        </Row>

        <Row gutter={16}>
          <Col md={24}>
            <Card title={
              <Row justify="space-between" align="middle" className="items-center">
                <Typography.Title level={4} className="!mb-0">
                  Returned COD Orders by State
                </Typography.Title>

                {permissions?.includes(PERMISSIONS.EXPORT_ORDER_LIST) && (
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={() => {
                      const params = new URLSearchParams({
                        internalstatus: ["Returned"],
                        startDate: filters.startDate,
                        endDate: filters.endDate,
                        codCurrencyCode: filters.codCurrencyCode
                      }).toString();

                      window.open(`/order/cod-order?${params}`, "_blank");
                    }}
                  >
                    CSV
                  </Button>
                )}
              </Row>
            }>
              {loading ? (
                <Spin />
              ) : (
                <Bar
                  data={{
                    labels: Object.keys(stateStats).sort(),
                    datasets: (() => {
                      const months = Object.keys(stateStats).sort();
                      const states = Array.from(
                        new Set(months.flatMap((m) => Object.keys(stateStats[m])))
                      );

                      return states.map((state, i) => ({
                        label: state,
                        data: months.map((m) => stateStats[m][state]?.returned || 0),
                        borderColor: `hsl(${(i * 30 + 15) % 360}, 70%, 55%)`,
                        backgroundColor: `hsl(${(i * 30 + 15) % 360}, 70%, 55%)`,
                        fill: false,
                        tension: 0.3,
                        borderWidth: 2,
                        pointRadius: 3,
                      }));
                    })(),
                  }}
                  options={{
                    responsive: true,
                    interaction: { mode: "index", intersect: false },
                    plugins: {
                      legend: { position: "bottom" },
                      title: {
                        display: true,
                        text: "Returned COD Orders by State (Monthly)",
                      },
                      tooltip: {
                        callbacks: {
                          label: (ctx) =>
                            `${ctx.dataset.label}: ${ctx.formattedValue}`,
                        },
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        title: { display: true, text: "Returned Orders" },
                      },
                      x: {
                        title: { display: true, text: "Month (YYYY-MM)" },
                      },
                    },
                  }}
                />
              )}
            </Card>
          </Col>
        </Row></Card>






    </React.Fragment>
  );
};

export default CodAnalysis;
